// Contrato de Gobernanza Soberana — vista admin (read-only) para legal.
// Muestra, por conversación/turno, el "contrato" firmado de LO QUE VIAJA al
// modelo soberano: lo que ENVIAMOS (modelo · org_id · grounded · firmas sha256)
// y lo que RESPONDE el gateway (verdict + sello). Sin PII: solo firmas.

import { useEffect, useState } from 'react';
import { ShieldCheck, Copy, RefreshCw, Search, Check } from 'lucide-react';
import { governanceApi, type GovernanceContract } from './api';
import { OsApp, osActionBtn } from '@/modules/orion-shell/OsApp';

function shortSig(s?: string | null): string {
  if (!s) return '—';
  const h = s.replace(/^sha256:/, '');
  return `sha256:${h.slice(0, 10)}…${h.slice(-6)}`;
}
function maskPhone(s?: string | null): string {
  if (!s) return '—';
  return s.length > 4 ? `…${s.slice(-4)}` : s;
}

function Badge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
        ok
          ? 'bg-primary/15 text-primary border-primary/30'
          : 'bg-muted text-muted-foreground border-border'
      }`}
    >
      {ok ? '✓' : '—'} {label}
    </span>
  );
}

function VerdictBadge({ verdict }: { verdict?: string | null }) {
  const v = (verdict || '').toLowerCase();
  const cls =
    v === 'allowed'
      ? 'bg-primary/15 text-primary border-primary/30'
      : v === 'withheld' || v === 'flagged'
        ? 'bg-destructive/15 text-destructive border-destructive/30'
        : 'bg-muted text-muted-foreground border-border';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border font-mono ${cls}`}>
      {verdict || 'sin verdict'}
    </span>
  );
}

function ContractCard({ c }: { c: GovernanceContract }) {
  const [copied, setCopied] = useState(false);
  const sent = c.sent || {};
  const rec = c.received || {};
  const seal = (rec.seal || {}) as Record<string, unknown>;
  const decisionId = typeof seal.decision_id === 'string' ? seal.decision_id : undefined;
  const isUcognet = (sent.model || '').startsWith('ucognet');
  const governed = isUcognet && !!sent.org_id;

  const copy = () => {
    navigator.clipboard?.writeText(JSON.stringify(c, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-xs text-muted-foreground font-mono">
          {c.timestamp ? new Date(c.timestamp).toLocaleString() : '—'} · {c.channel || 'chat'} · tel {maskPhone(c.session_id)}
        </div>
        <button
          onClick={copy}
          className="text-xs text-primary flex items-center gap-1 hover:underline shrink-0"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Copiado' : 'Copiar para legal'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* ENVIADO */}
        <div>
          <div className="text-xs font-semibold text-foreground mb-2">ENVIADO · nuestro lado (firmado)</div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            <Badge ok={isUcognet} label={`modelo: ${sent.model || '—'}`} />
            <Badge ok={!!sent.org_id} label={`org_id: ${sent.org_id || '—'}`} />
            <Badge ok={!!sent.grounded} label="grounded" />
          </div>
          <dl className="text-xs space-y-1 font-mono text-muted-foreground break-all">
            <div>firma_prompt: {shortSig(sent.prompt_signature)}</div>
            <div className="text-foreground">firma_contrato: {shortSig(sent.contract_signature)}</div>
          </dl>
        </div>

        {/* RECIBIDO */}
        <div className="md:border-l md:border-border md:pl-4">
          <div className="text-xs font-semibold text-foreground mb-2">RECIBIDO · gateway soberano</div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            <VerdictBadge verdict={rec.verdict} />
          </div>
          <dl className="text-xs space-y-1 font-mono text-muted-foreground break-all">
            <div>decision_id: {decisionId || '—'}</div>
          </dl>
          {rec.seal && Object.keys(seal).length > 0 && (
            <details className="mt-2">
              <summary className="text-xs text-primary cursor-pointer">ver sello completo</summary>
              <pre className="text-[10px] mt-1 bg-muted p-2 rounded-md overflow-x-auto max-h-40 text-muted-foreground">
                {JSON.stringify(rec.seal, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>

      {governed && (
        <div className="mt-3 text-xs text-primary flex items-center gap-1">
          <ShieldCheck className="h-3.5 w-3.5" /> Gobernado: modelo soberano + org_id presente en el request.
        </div>
      )}
    </div>
  );
}

export function GovernancePage() {
  const [contracts, setContracts] = useState<GovernanceContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const load = () => {
    setLoading(true);
    setErr(null);
    governanceApi
      .getContracts({ limit: 100 })
      .then((r) => setContracts(r.contracts || []))
      .catch((e) => setErr(e?.message || 'No se pudo cargar el contrato.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = q ? contracts.filter((c) => (c.session_id || '').includes(q)) : contracts;

  return (
    <OsApp
      icon={ShieldCheck}
      title="Contrato de Gobernanza"
      subtitle="Lo que viaja al modelo gobernado, firmado (sha256) y verificable · sin datos personales"
      actions={
        <button type="button" onClick={load} disabled={loading} className={osActionBtn}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden /> Actualizar
        </button>
      }
    >
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por teléfono / conversación…"
          className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-1.5 text-sm text-foreground"
        />
      </div>

      {loading && <div className="text-sm text-muted-foreground">Cargando contratos…</div>}
      {err && <div className="text-sm text-destructive">{err}</div>}
      {!loading && !err && !filtered.length && (
        <div className="text-sm text-muted-foreground">Sin contratos registrados todavía.</div>
      )}

      <div className="space-y-3">
        {filtered.map((c, i) => (
          <ContractCard key={`${c.session_id}-${c.timestamp}-${i}`} c={c} />
        ))}
      </div>
    </div>
    </OsApp>
  );
}
